import React, { forwardRef, memo, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { ThemeMode } from 'constants/themes';

import BottomBtnGroup from './BottomBtnGroup';
import { LoanTabLayoutTestIds } from './LoanTabLayout.testIds';

const additionalRadiusSx = { borderRadius: '20px' };
const defaultContent = [];

const LoanTabLayout = forwardRef(
  (
    {
      actionItem = null,
      id = null,
      top,
      content = defaultContent,
      children,
      isRowLayout = true,
      isEmptyContentPlaceholder = false,
      isLightBackgroundLayout = false,
      useBottomButtons = true,
      shouldAddRadius = false,
      isActivitiesPage = false,
      containerClassname = '',
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDarkTheme = theme.palette.type === ThemeMode.DARK;

    const tableContainerSx = useMemo(
      () => ({
        borderRadius: '5px',
        height: isActivitiesPage ? '100%' : 'inherit',
        ...(isLightBackgroundLayout
          ? {
              paddingLeft: '20px',
              paddingRight: '20px',
              backgroundColor: isDarkTheme
                ? theme.palette.grey[900]
                : theme.palette.background.default,
            }
          : {}),
        ...(shouldAddRadius ? additionalRadiusSx : {}),
      }),
      [isActivitiesPage, isLightBackgroundLayout, isDarkTheme, theme, shouldAddRadius],
    );

    const tableHeadSx = useMemo(
      () => ({
        borderBottom: `1px solid ${
          isDarkTheme ? theme.palette.grey[500] : theme.palette.grey[400]
        }`,
      }),
      [isDarkTheme, theme],
    );

    return (
      <div className={clsx(isActivitiesPage && 'h-full')} data-testid={id} ref={ref}>
        <Box className={clsx(containerClassname, isRowLayout && 'pb-28')} sx={tableContainerSx}>
          {!!top && (
            <Box
              className={clsx(`hidden md:flex ${top ? 'py-28' : 'py-12'} pl-24 pr-4`)}
              data-testid={LoanTabLayoutTestIds.LAYOUT_HEADER}
              sx={tableHeadSx}
            >
              {top}
            </Box>
          )}

          {isEmptyContentPlaceholder && (
            <Typography className={`${top ? 'pt-28' : 'pt-12'} pl-24 pr-4`} variant="regular14">
              No Data
            </Typography>
          )}

          {!isEmptyContentPlaceholder && (children ?? content)}
        </Box>

        {useBottomButtons && <BottomBtnGroup actionItem={actionItem} />}
      </div>
    );
  },
);

LoanTabLayout.propTypes = {
  actionItem: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]),
  top: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]),
  content: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]),
  isRowLayout: PropTypes.bool,
  isEmptyContentPlaceholder: PropTypes.bool,
  isLightBackgroundLayout: PropTypes.bool,
  useBottomButtons: PropTypes.bool,
  id: PropTypes.string,
  shouldAddRadius: PropTypes.bool,
  isActivitiesPage: PropTypes.bool,
  containerClassname: PropTypes.string,
};

export default memo(LoanTabLayout);
